/**
 * The permission catalogue, mirroring `Modules\Authorization\Enums\Permission`
 * on the API.
 *
 * These strings are a contract with the backend. They live as a typed const so a
 * typo in a route guard is a compile error rather than a silently hidden menu
 * item that nobody notices until an editor reports they cannot find a page.
 *
 * Content is one entity (`contents.*`), not separate `articles.*` / `pages.*`
 * permissions — that split would imply a data model the API does not have.
 */
export const PERMISSIONS = {
  contents: {
    view: 'contents.view',
    create: 'contents.create',
    update: 'contents.update',
    delete: 'contents.delete',
    publish: 'contents.publish',
    translate: 'contents.translate',
    approveTranslation: 'contents.approve_translation',
  },
  gates: {
    view: 'gates.view',
    decide: 'gates.decide',
    waive: 'gates.waive',
  },
  taxonomy: {
    view: 'taxonomy.view',
    manage: 'taxonomy.manage',
  },
  authors: {
    view: 'authors.view',
    manage: 'authors.manage',
  },
  media: {
    view: 'media.view',
    upload: 'media.upload',
    manage: 'media.manage',
    delete: 'media.delete',
  },
  seo: {
    view: 'seo.view',
    manage: 'seo.manage',
  },
  keywords: {
    manage: 'keywords.manage',
  },
  redirects: {
    view: 'redirects.view',
    manage: 'redirects.manage',
  },
  navigation: {
    manage: 'navigation.manage',
  },
  locales: {
    view: 'locales.view',
    manage: 'locales.manage',
  },
  settings: {
    manage: 'settings.manage',
  },
  analytics: {
    manage: 'analytics.manage',
  },
  users: {
    view: 'users.view',
    manage: 'users.manage',
  },
  roles: {
    manage: 'roles.manage',
  },
  audit: {
    view: 'audit.view',
  },
  import: {
    view: 'import.view',
    manage: 'import.manage',
  },
} as const;

type Leaves<T> = T extends string ? T : T extends object ? Leaves<T[keyof T]> : never;

export type Permission = Leaves<typeof PERMISSIONS>;

/**
 * Role names seeded by the API. Used only for display and for the Super Admin
 * bypass; access decisions are always made on permissions, never on roles, so
 * that adding a role never requires touching the UI.
 */
export const ROLES = {
  superAdmin: 'super-admin',
  admin: 'admin',
  editor: 'editor',
  seoManager: 'seo-manager',
  translator: 'translator',
  author: 'author',
  viewer: 'viewer',
} as const;

export type RoleName = (typeof ROLES)[keyof typeof ROLES];
